<template>
  <div class="main">
    <div class="text-right">
      <div
        v-on:click="$router.replace({ name: 'CreditCardView' })"
        class="upper-close px-3 pt-1"
      >
        <i class="fa-solid fa-x"></i>
      </div>
    </div>
    <div class="container px-3 h-100">
      <CcPinInput
        data-testid="change-pin-input"
        :text="t('PAYJOY_CARD_VIEWS.CC_PING_CHANGE_DESCRIPTION')"
        v-model="newPin"
      />
      <div class="mt-5">
        <CollapseCard
          v-if="pinFlow === 'confirm_in_atm'"
          fill
          default-open
          bold-title
          :title="t('PAYJOY_CARD_VIEWS.CC_PING_CHANGE_DISCLAIMER_TITLE')"
        >
          <p
            v-html="
              t('PAYJOY_CARD_VIEWS.CC_PING_CHANGE_DISCLAIMER_DESCRIPTION')
            "
          ></p>
          <div class="col mt-3">
            <img
              class="my-2 p-0 d-block w-100"
              :src="useAsset('img/credit-line/cc_pin_change_bank_logos.svg')"
              alt="key"
            />
          </div>
        </CollapseCard>
        <CollapseCard
          v-if="pinFlow === 'direct'"
          fill
          default-open
          bold-title
          :title="t('PAYJOY_CARD_VIEWS.CC_PING_CHANGE_DISCLAIMER_TITLE')"
        >
          <p
            v-html="
              t('PAYJOY_CARD_VIEWS.CC_PING_CHANGE_DISCLAIMER_DESCRIPTION_CO')
            "
          ></p>
        </CollapseCard>
      </div>
      <Button
        :disabled="!newPin"
        :click-handler="onSubmit"
        class="w-100 mx-auto"
        data-testid="submit"
      >
        {{ t("PAYJOY_CARD_VIEWS.CC_PING_CHANGE_BTN") }}
      </Button>
      <Button
        :click-handler="onDismiss"
        :secondary="true"
        class="w-100 mx-auto"
        data-testid="dismiss"
      >
        {{ t("CANCEL") }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { useRouter } from "vue-router";
import CcPinInput from "@/apps/credit-line/components/CreditCard/PinInput.vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import CollapseCard from "@/apps/credit-line/components/CollapseCard.vue";
import Button from "@/apps/credit-line/components/CreditPull/Button.vue";
import { Service } from "@/apps/credit-line/services/network.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { getSettings } from "@/apps/credit-line/helpers/consumerSettings.helper";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";
import { DEFAULT_SETTING } from "@/shared-models/consumer/ISetting";

declare const Android: AndroidInterface;

const { t } = useI18n();
const router = useRouter();
const CreditLineService = new Service();
const newPin = ref("");
const pinFlow = ref<string[]>();

onMounted(() => {
  try {
    Android.finishedLoading();
    // eslint-disable-next-line no-empty
  } catch (_) {}
  initializeCardsPageSettings();
});

const initializeCardsPageSettings = async () => {
  const cardsPageSettings = await getSettings(SettingsCategory.CL_CARDS_PAGE);
  const pinChangeSettings =
    cardsPageSettings["cardPinChange"] ?? DEFAULT_SETTING;

  pinFlow.value = pinChangeSettings?.values?.type as string;
};

const onDismiss = () => {
  router.replace({
    name: "CreditCardView",
  });
};

const onSubmit = async () => {
  try {
    const res = await CreditLineService.updateCcPin({
      deviceTag: AuthenticationHelper?.getDeviceTag() || "",
      pin: newPin.value
    });
    if (res.data?.id) {
      router.replace({
        name: "CreditCardPinChangeResultView",
        query: {
          success: "1",
        },
      });
    } else {
      router.replace({
        name: "CreditCardPinChangeResultView",
        query: {
          success: "0",
        },
      });
    }
  } catch (error) {
    ErrorHandler.handleNetworkErrorAndLog(error);
    router.replace({
      name: "CreditCardPinChangeResultView",
      query: {
        success: "0",
      },
    });
  }
};
</script>

<style lang="scss" scoped></style>
